import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { supportAPI } from '../services/api';

interface FormState {
  subject: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
}

const HelpSupport: React.FC = () => {
  const [form, setForm] = useState<FormState>({
    subject: '',
    description: '',
    priority: 'Medium',
  });
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketKey, setTicketKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setScreenshot(file);
  };

  const handleRemoveFile = () => {
    setScreenshot(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTicketKey(null);

    if (!form.subject.trim()) {
      setError('Subject is required.');
      return;
    }
    if (!form.description.trim()) {
      setError('Description is required.');
      return;
    }

    const data = new FormData();
    data.append('subject', form.subject.trim());
    data.append('description', form.description.trim());
    data.append('priority', form.priority);
    if (screenshot) {
      data.append('screenshot', screenshot);
    }

    setSubmitting(true);
    try {
      const res = await supportAPI.submitTicket(data);
      setTicketKey(res.data.ticket_key);
      setForm({ subject: '', description: '', priority: 'Medium' });
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to submit ticket. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <HelpOutlineIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5">Help & Support</Typography>
      </Stack>

      <Stack spacing={3} maxWidth={720}>
        {ticketKey && (
          <Alert
            severity="success"
            onClose={() => setTicketKey(null)}
          >
            Your support ticket <strong>{ticketKey}</strong> has been created successfully. Our team will review it shortly.
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <CardHeader
            title="Submit a Support Request"
            subheader="Describe your issue and we'll create a ticket for our technical team."
          />
          <Divider />
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  label="Subject"
                  value={form.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  required
                  fullWidth
                  placeholder="Brief summary of the issue"
                />

                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  required
                  fullWidth
                  multiline
                  minRows={5}
                  placeholder="Please describe the issue in detail — steps to reproduce, expected vs actual behaviour, etc."
                />

                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={form.priority}
                    label="Priority"
                    onChange={(e) => handleChange('priority', e.target.value as FormState['priority'])}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Screenshot (optional — jpg, png, gif, pdf, max 10 MB)
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  {screenshot ? (
                    <Chip
                      icon={<AttachFileIcon />}
                      label={screenshot.name}
                      onDelete={handleRemoveFile}
                      variant="outlined"
                    />
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Attach File
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{ minWidth: 160 }}
                  >
                    {submitting ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress size={16} color="inherit" />
                        <span>Submitting...</span>
                      </Stack>
                    ) : (
                      'Submit Ticket'
                    )}
                  </Button>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              What happens next?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once you submit, a JIRA ticket is created and assigned to our technical team. You will
              receive updates via your registered email address. For urgent issues, please mark the
              priority as <strong>High</strong>.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default HelpSupport;
